/*
 * Copyright 2017-present Evernote Corporation. All rights reserved.
 */

import debug from 'debug';

// eslint-disable-next-line no-use-before-define
let gTransport: (logger: Logger) => void = bindLogger;

/**
 * Logging system for Ion to reduce memory leaks
 */
export class Logger {
  static existingLoggers: { [topic: string]: Logger } = {};
  topicStr!: string;
  _debugOut!: (message: any, ...args: unknown[]) => { enabled: boolean } | void;
  log!: (message: any, ...args: unknown[]) => void;
  info!: (message: any, ...args: unknown[]) => void;
  warn!: (message: any, ...args: unknown[]) => void;
  error!: (message: any, ...args: unknown[]) => void;
  trace!: (message: any, ...args: unknown[]) => void;
  automation!: (message: any, ...args: unknown[]) => void;

  static rebindAllLoggers() {
    Object.keys(Logger.existingLoggers).forEach(topicStr => {
      gTransport(Logger.existingLoggers[topicStr]);
    });
  }

  constructor(topicStr: string) {
    if (topicStr.length === 0) {
      throw new Error('Logger: A topic string must be provided on init');
    }

    if (Logger.existingLoggers[topicStr]) {
      return Logger.existingLoggers[topicStr];
    }

    this.topicStr = topicStr;
    this._debugOut = debug(topicStr);
    gTransport(this);

    Logger.existingLoggers[topicStr] = this;
  }

  safeLog(...args: unknown[]) {
    if (this.isInitialized()) {
      // @ts-ignore ION-8710
      this._debugOut(...makeArgsMemorySafe(args));
    }
  }

  isInitialized() {
    // @ts-ignore ION-8710
    return this._debugOut != null && this._debugOut.enabled;
  }
}

function warn(...args: unknown[]) {
  // @ts-ignore ION-8710
  if (this.isInitialized()) {
    // eslint-disable-next-line no-console
    console.warn(...makeArgsMemorySafe(args));
  }
}

/*
 * Note: we always show errors, even if the topic is not enabled.
 */
function error(...args: unknown[]) {
  // eslint-disable-next-line no-console
  console.error(...makeArgsMemorySafe(args));
}

function trace(...args: unknown[]) {
  // @ts-ignore ION-8710
  if (this.isInitialized()) {
    // eslint-disable-next-line no-console
    console.trace(...makeArgsMemorySafe(args));
  }
}

type Transport = (logger: Logger) => void;

export function enable(namespace: string) {
  debug.enable(namespace);
}

function bindLogger(logger: Logger) {
  logger.log = logger.safeLog.bind(logger, logger.topicStr);
  logger.info = logger.safeLog.bind(logger, logger.topicStr);
  // Automation log level logs without the topic string included so the first argument can
  // be parsed by webdriver. Included in logger so we can enable automation logging same
  // way as other debug logs.
  logger.automation = logger.safeLog.bind(logger);
  if (process.env.NODE_ENV === 'production') {
    logger.warn = warn.bind(logger, logger.topicStr);
    logger.error = error.bind(logger, logger.topicStr);
    logger.trace = trace.bind(logger, logger.topicStr);
  } else {
    // keep line number references
    /* eslint-disable */
    logger.warn = console.warn.bind(console, logger.topicStr);
    logger.error = console.error.bind(console, logger.topicStr);
    logger.trace = console.trace.bind(console, logger.topicStr);
    /* eslint-enable */
  }
}

function makeArgsMemorySafe(args: any[]): any[] {
  return args.map(a => (a instanceof Object ? JSON.stringify(a) : a));
}

export function setTransport(newTransport: Transport) {
  gTransport = newTransport;
  Logger.rebindAllLoggers();
}

export default function createLogger(topicName: string) {
  return new Logger(topicName);
}
